import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DetectedFileType, FILE_TYPE_LABELS, FILE_TYPE_MODULES } from './types';
import './DetectedFileTypeDropdown.scss';

// All available detected file types for the dropdown
const DETECTED_FILE_TYPE_OPTIONS: DetectedFileType[] = [
  'tms',
  'vehicle_metrics',
  'transport_order_add',
  'cpi',
  'fuel',
  'hoc',
  'unknown',
];

interface DetectedFileTypeDropdownProps {
  value: DetectedFileType;
  onChange: (type: DetectedFileType) => void;
  disabled?: boolean;
  showModule?: boolean;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

const DetectedFileTypeDropdown: FC<DetectedFileTypeDropdownProps> = ({
  value,
  onChange,
  disabled = false,
  showModule = true,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  // The menu is rendered in a portal with fixed positioning so it can escape
  // the scrollable file-list container (overflow-y: auto would otherwise clip
  // it). Recompute the anchor position whenever it opens, scrolls, or resizes.
  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    // Capture phase so we also catch scrolls on the inner file-list container.
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside (account for the portalled menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (type: DetectedFileType) => {
    onChange(type);
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const getModuleBadgeClass = (type: DetectedFileType) => {
    const module = FILE_TYPE_MODULES[type];
    if (!module) return '';
    return `detected-file-type-dropdown__module--${module.toLowerCase()}`;
  };

  return (
    <div
      className={`detected-file-type-dropdown ${disabled ? 'detected-file-type-dropdown--disabled' : ''}`}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <button
        type="button"
        ref={buttonRef}
        className={`detected-file-type-dropdown__button ${isOpen ? 'detected-file-type-dropdown__button--open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="detected-file-type-dropdown__value">
          {showModule && FILE_TYPE_MODULES[value] && (
            <span className={`detected-file-type-dropdown__module ${getModuleBadgeClass(value)}`}>
              {FILE_TYPE_MODULES[value]}
            </span>
          )}
          {t(FILE_TYPE_LABELS[value])}
        </span>
        <span className="detected-file-type-dropdown__arrow">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </span>
      </button>

      {isOpen && menuPosition && createPortal(
        <ul
          ref={menuRef}
          className="detected-file-type-dropdown__menu detected-file-type-dropdown__menu--floating"
          role="listbox"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
          }}
        >
          {DETECTED_FILE_TYPE_OPTIONS.map((type) => (
            <li
              key={type}
              className={`detected-file-type-dropdown__option ${value === type ? 'detected-file-type-dropdown__option--selected' : ''}`}
              role="option"
              aria-selected={value === type}
              onClick={() => handleSelect(type)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSelect(type);
                }
              }}
              tabIndex={0}
            >
              {showModule && FILE_TYPE_MODULES[type] && (
                <span className={`detected-file-type-dropdown__module ${getModuleBadgeClass(type)}`}>
                  {FILE_TYPE_MODULES[type]}
                </span>
              )}
              {t(FILE_TYPE_LABELS[type])}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default DetectedFileTypeDropdown;
